import { KitDeletionArea } from "@/components/KitDeletionArea";
import { ProfilePictureSelector } from "@/components/ProfilePictureSelector";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { ArtifactMetadata } from "@/components/display/attributes/ArtifactMetadata";
import DisplayEditor from "@/components/display/display-editor/DisplayEditor";
import { GrantDialog } from "@/components/grant/GrantDialog";
import { ExportKitArea } from "@/components/kit/ExportKitArea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EXPERT_TYPE_LABELS } from "@/consts/experts";
import { useExpertEditData } from "@/data/use-expert-edit-data";
import { useExpertData } from "@/data/use-experts-data";
import { useKitData } from "@/data/use-kits-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { Expert } from "@/types/types";
import { toast } from "sonner";

export default function ExpertSettingsPage() {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { kit, updateKit } = useKitData(groupUuid, kitUuid);
  const { expertUuid } = useExpertEditData({ groupUuid, kitUuid });
  const { updateExpert, expert } = useExpertData(
    groupUuid,
    kitUuid,
    expertUuid,
  );

  if (!expert || !groupUuid) {
    return null;
  }

  return (
    <>
      <AppTopRightMenu.ContentPortal display="append">
        <GrantDialog
          groupUuid={groupUuid}
          objectName={expert.name}
          expertUuid={expert.uuid}
          notice={
            expert.dependencies.length > 0
              ? "This expert can use its dependencies in any group it is shared with, even when those skills, files, and templates are not shared there."
              : undefined
          }
        />
      </AppTopRightMenu.ContentPortal>
      <Page>
        <PageHeader>
          <PageHeader.Title>Expert Settings</PageHeader.Title>
        </PageHeader>
        <PageBody innerClassName="gap-8">
          <PageBody.Metadata className="justify-end">
            <ArtifactMetadata artifact={expert} />
          </PageBody.Metadata>
          <DisplayEditor
            name={expert.name}
            description={expert.description}
            onNameChange={async (name) => {
              await updateExpert({ name });
              await updateKit({ name });
            }}
            onDescriptionChange={async (description) => {
              await updateExpert({ description });
              await updateKit({ description });
            }}
          />

          <div className="flex flex-col gap-2">
            <Label htmlFor="expert-type">Type</Label>
            <Select
              value={expert.expertType}
              onValueChange={async (value: Expert["expertType"]) => {
                try {
                  await updateExpert({ expertType: value });
                  toast.success("Expert type updated");
                } catch (error) {
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : "Failed to update expert type",
                  );
                }
              }}
            >
              <SelectTrigger id="expert-type">
                <SelectValue placeholder="Select expert type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EXPERT_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ProfilePictureSelector
            profilePicture={expert.profilePicture}
            onProfilePictureChange={(profilePicture) => {
              updateExpert({ profilePicture });
            }}
          />

          {kit && (
            <>
              <ExportKitArea kit={kit} />
              <KitDeletionArea kit={kit} />
            </>
          )}
        </PageBody>
      </Page>
    </>
  );
}
